import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Box,
  Button,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  FormControl,
  FormLabel,
  Checkbox,
  Stack,
  Divider,
  Card,
  CardContent,
  Grid,
  RadioGroup,
  Radio,
  Alert,
  Slider
} from '@mui/joy';
import {
  Clock as ScheduleIcon,
  Robot as AIIcon,
  Folder as TopicIcon,
  Gear as SettingsIcon,
  Image as ImageIcon,
  Buildings as BusinessIcon
} from '@phosphor-icons/react';

interface SocialMarketingSettings {
  social_auto_generate: boolean;
  social_require_review: boolean;
  generation_frequency: string;
  social_posting_enabled: boolean;
  social_posts_per_week: number;
  social_posting_times: string[];
  social_post_types: string[];
}

interface SocialAutoSettingsModalProps {
  open: boolean;
  onClose: () => void;
  settings: SocialMarketingSettings | null;
  onSave: (settings: Partial<SocialMarketingSettings>) => Promise<void>;
}

export const SocialAutoSettingsModal: React.FC<SocialAutoSettingsModalProps> = ({
  open,
  onClose,
  settings,
  onSave
}) => {
  const [activeTab, setActiveTab] = useState('schedule');
  const [formData, setFormData] = useState<Partial<SocialMarketingSettings>>({
    generation_frequency: 'weekly',
    social_posts_per_week: 4,
    social_require_review: true,
    social_posting_times: ['10:00', '14:00', '18:00'],
    social_post_types: ['project_showcase', 'tip_advice', 'company_culture']
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setFormData({
        generation_frequency: 'weekly', // Always use weekly
        social_posts_per_week: settings.social_posts_per_week || 4,
        social_require_review: settings.social_require_review !== undefined ? settings.social_require_review : true,
        social_posting_times: settings.social_posting_times?.length ? settings.social_posting_times : ['10:00', '14:00', '18:00'],
        social_post_types: settings.social_post_types?.length ? settings.social_post_types : ['project_showcase', 'tip_advice', 'company_culture']
      });
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const postTypes = [
    { value: 'project_showcase', label: 'Project Showcase', icon: <ImageIcon />, description: 'Before/after photos and project highlights' },
    { value: 'tip_advice', label: 'Tips & Advice', icon: <TopicIcon />, description: 'Helpful roofing tips and maintenance advice' },
    { value: 'company_culture', label: 'Company Culture', icon: <SettingsIcon />, description: 'Team photos and company values' },
    { value: 'testimonial', label: 'Customer Testimonials', icon: <BusinessIcon />, description: 'Customer reviews and success stories' }
  ];

  const timeSlots = [
    '06:00', '07:00', '08:00', '09:00', '10:00', '11:00',
    '12:00', '13:00', '14:00', '15:00', '16:00', '17:00',
    '18:00', '19:00', '20:00', '21:00'
  ];

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="lg" sx={{ width: '90%', maxWidth: 800 }}>
        <ModalClose />
        <Typography level="h3">Social Media Auto-Generation Settings</Typography>
        
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as string)} sx={{ mt: 2 }}>
          <TabList>
            <Tab value="schedule">
              <ScheduleIcon style={{ marginRight: 8 }} />
              Schedule
            </Tab>
            <Tab value="content">
              <AIIcon style={{ marginRight: 8 }} />
              Content
            </Tab>
            <Tab value="review">
              <SettingsIcon style={{ marginRight: 8 }} />
              Review & Approval
            </Tab>
          </TabList>

          {/* Schedule Tab */}
          <TabPanel value="schedule">
            <Stack spacing={3}>
              <FormControl>
                <FormLabel>Posts Per Week</FormLabel>
                <Box sx={{ px: 2 }}>
                  <Slider
                    value={formData.social_posts_per_week || 4}
                    onChange={(_, value) => setFormData({ ...formData, social_posts_per_week: value as number })}
                    min={1}
                    max={8}
                    marks
                    valueLabelDisplay="on"
                    sx={{ mt: 2 }}
                  />
                </Box>
                <Typography level="body-sm" sx={{ mt: 1 }}>
                  Recommended: 3-5 posts per week for optimal engagement
                </Typography>
              </FormControl>

              <FormControl>
                <FormLabel>Preferred Posting Times</FormLabel>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {timeSlots.map((time) => (
                    <Grid key={time} xs={3}>
                      <Checkbox
                        checked={formData.social_posting_times?.includes(time) || false}
                        onChange={(e) => {
                          const currentTimes = formData.social_posting_times || [];
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              social_posting_times: [...currentTimes, time]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              social_posting_times: currentTimes.filter(t => t !== time)
                            });
                          }
                        }}
                        label={time}
                        size="sm"
                      />
                    </Grid>
                  ))}
                </Grid>
                <Typography level="body-sm" sx={{ mt: 1 }}>
                  Select optimal times for your audience (recommended: morning, lunch, evening)
                </Typography>
              </FormControl>
            </Stack>
          </TabPanel>

          {/* Content Tab */}
          <TabPanel value="content">
            <Stack spacing={3}>
              <FormControl>
                <FormLabel>Post Types to Generate</FormLabel>
                <Typography level="body-sm" sx={{ mb: 2 }}>
                  Choose which types of content to automatically generate
                </Typography>
                <Grid container spacing={2}>
                  {postTypes.map((type) => (
                    <Grid key={type.value} xs={12} md={6}>
                      <Card
                        variant={formData.social_post_types?.includes(type.value) ? 'solid' : 'outlined'}
                        color={formData.social_post_types?.includes(type.value) ? 'primary' : 'neutral'}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          const currentTypes = formData.social_post_types || [];
                          if (currentTypes.includes(type.value)) {
                            setFormData({
                              ...formData,
                              social_post_types: currentTypes.filter(t => t !== type.value)
                            });
                          } else {
                            setFormData({
                              ...formData,
                              social_post_types: [...currentTypes, type.value]
                            });
                          }
                        }}
                      >
                        <CardContent sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Checkbox
                              checked={formData.social_post_types?.includes(type.value) || false}
                              readOnly
                            />
                            {type.icon}
                            <Box>
                              <Typography level="title-sm">{type.label}</Typography>
                              <Typography level="body-xs">{type.description}</Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </FormControl>

              <Alert color="primary" variant="soft">
                <AIIcon />
                Posts will be generated using photos tagged with "marketing" from your organization's media library.
                AI will analyze the images to create relevant, engaging content that matches your brand voice.
              </Alert>
            </Stack>
          </TabPanel>

          {/* Review & Approval Tab */}
          <TabPanel value="review">
            <Stack spacing={3}>
              <FormControl>
                <FormLabel>Content Review</FormLabel>
                <RadioGroup
                  value={formData.social_require_review ? 'review' : 'auto'}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    social_require_review: e.target.value === 'review' 
                  })}
                >
                  <Radio value="review" label="Require manual review before posting" />
                  <Radio value="auto" label="Auto-publish generated posts" />
                </RadioGroup>
                <Typography level="body-sm" sx={{ mt: 1 }}>
                  {formData.social_require_review 
                    ? 'Generated posts will be saved as drafts for your review and approval.'
                    : 'Generated posts will be automatically scheduled and published.'}
                </Typography>
              </FormControl>

              <Divider />

              <Card variant="soft" color="warning">
                <CardContent>
                  <Typography level="title-sm" sx={{ mb: 1 }}>
                    Quality & Brand Consistency
                  </Typography>
                  <Typography level="body-sm">
                    Our AI uses your organization's brand voice and context to generate content, but we recommend 
                    enabling manual review to ensure all posts meet your quality standards and brand guidelines.
                  </Typography>
                </CardContent>
              </Card>
            </Stack>
          </TabPanel>
        </Tabs>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="plain" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            onClick={handleSave}
            loading={saving}
            startDecorator={<SettingsIcon />}
          >
            Save Settings
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
};